import type { Competitor } from '../types'

// Sources (checked 2026-10-01): bio.link homepage pricing section (bio.link/pricing
// is a user profile), bio.link/features and /faq, help.bio.link articles, the
// app.bio.link checkout and embed menu, App Store and Google Play listings.
const biolink: Competitor = {
  slug: 'biolink',
  aliases: ['bio-link'],
  name: 'bio.link',
  tagline: 'Link in bio with an AI assistant and an email list, sold as one Pro plan.',
  site: 'https://bio.link',
  pricingUrl: 'https://bio.link',
  checkedOn: '2026-10-01',
  seo: {
    title: 'bio.link alternative: mychar.gg vs bio.link compared (2026)',
    description: 'A bio.link alternative with a free plan and one-time upgrades. Compare design, music, video clips, integrations, analytics, and price with bio.link Pro.'
  },
  hero: {
    title: 'The bio.link alternative with a free plan that stays free',
    description: 'Design your page in a live drag-and-drop editor with effects, music, and clips. Start free, and if you upgrade, pay once instead of every month.'
  },
  summary: 'mychar.gg is a bio.link alternative with a free plan and one-time upgrades: Pro is €6.99 once, while bio.link sells a single Pro subscription at $14.99 a month or $7.49 a month billed yearly. mychar.gg goes further on design and on-page content, with animated effects, custom cursors, a music player, and video clips. bio.link is ahead on its AI assistant, email list, and mobile apps.',
  chooseMychar: [
    'want a free page, not a free trial',
    'want animated effects, custom cursors, and a music player',
    'want video clips that play on the page',
    'would rather pay once for upgrades'
  ],
  chooseThem: [
    'want an AI assistant that answers visitor questions',
    'want an email list and posts with subscriber alerts',
    'want to edit from a native phone app'
  ],
  features: {
    free_plan: { yes: false, text: 'Not listed, trial on yearly' },
    paid_plans: 'Pro $14.99 a month or $7.49 yearly',
    billing: 'Monthly or yearly subscription',
    first_year: '$89.88 (Pro, billed yearly)',
    custom_domain: { yes: true, text: 'Included in Pro' },
    remove_branding: { yes: true, text: 'Included in Pro' },
    editor: 'Dashboard tabs with a live preview',
    drag_drop: { yes: true, text: 'Rearrange sections' },
    themes: { yes: true, text: '15+ themes and a theme designer' },
    effects: { yes: false, text: 'GIF backgrounds' },
    cursors: false,
    video_background: { yes: true, text: 'Stock videos' },
    custom_code: false,
    any_url: true,
    music_player: { yes: false, text: 'Spotify and SoundCloud embeds' },
    video_clips: { yes: false, text: 'YouTube and Vimeo embeds' },
    comments: false,
    qr_code: true,
    youtube: 'Video embeds',
    twitch: 'Stream embeds',
    discord: { yes: false, text: 'Link only' },
    music_embeds: { yes: true, text: 'Spotify and SoundCloud' },
    analytics_live: 'Locations and traffic sources',
    store: { yes: false, text: 'Shopify embed only' },
    tips: 'Buy Me a Coffee embed',
    email_capture: { yes: true, text: 'Email list and forms' },
    mobile_app: { yes: true, text: 'iOS and Android' }
  },
  plans: [
    { name: 'Pro, monthly', price: '$14.99', period: '/month', note: 'Every feature: unlimited pages, AI assistant, custom domain, no branding, email list.' },
    { name: 'Pro, yearly', price: '$7.49', period: '/month, billed yearly', note: 'The same plan. bio.link shows the yearly price as a limited-time offer, with a 7-day trial.' }
  ],
  plansNote: 'USD. bio.link lists one Pro plan; its site does not show a free tier.',
  advantages: [
    {
      title: 'A free plan',
      description: 'Build and publish your page for free, forever, with no card. bio.link shows only its Pro subscription and a trial.',
      icon: 'i-lucide-gift'
    },
    {
      title: 'Pay once',
      description: 'Pro is €6.99 and Elite €12.99, both one-time. bio.link Pro is $89.88 a year at its yearly price.',
      icon: 'i-lucide-badge-euro'
    },
    {
      title: 'Animated effects and cursors',
      description: 'Rain, snow, fire, sakura, plasma, and more, plus custom cursors and cursor trails. bio.link uses GIF and stock video backgrounds.',
      icon: 'i-lucide-sparkles'
    },
    {
      title: 'A music player and real video clips',
      description: 'Upload tracks that play while people look around and clips that play in place, instead of embedding them from other services.',
      icon: 'i-lucide-music'
    },
    {
      title: 'Discord and live Twitch',
      description: 'Show your Discord profile and your Twitch live status and schedule. On bio.link, Discord is a link icon.',
      icon: 'i-lucide-radio'
    },
    {
      title: 'Community features',
      description: 'Comments, linked friends, achievements, a view counter, a game library, and a setup section.',
      icon: 'i-lucide-users'
    }
  ],
  strengths: [
    {
      title: 'An AI assistant on your page',
      description: 'bio.link can answer visitor questions from your own content, and its analytics count those conversations.'
    },
    {
      title: 'An email list and posts',
      description: 'You can collect subscribers, publish posts, and alert subscribers when something new goes up. mychar.gg has no email tools.'
    },
    {
      title: 'Everything in one plan',
      description: 'Pro includes unlimited pages, a custom domain, and no branding, so there is only one decision to make.'
    },
    {
      title: 'Native mobile apps',
      description: 'bio.link has iOS and Android apps for editing on the go. mychar.gg works in the browser on any device.'
    }
  ],
  sections: [
    {
      title: 'Why people look for a bio.link alternative',
      paragraphs: [
        'bio.link used to be known as a simple, free link page. Today its site sells one Pro plan, at $14.99 a month or $7.49 a month on the yearly price, with a 7-day trial, and the product has moved toward an AI assistant that talks to your visitors. If you came for a clean page you can design and keep for free, that shift is the usual reason to look around.',
        'The design side is the other one. bio.link offers themes, a theme designer, GIF backgrounds, and stock video backgrounds, and it embeds media from YouTube, Spotify, and SoundCloud. It does not have animated effects, custom cursors, a built-in music player, or uploaded video clips.'
      ]
    },
    {
      title: 'How mychar.gg is different',
      paragraphs: [
        'mychar.gg keeps a real free plan: your page, unlimited links to about 35 platforms, a music player, a Discord section, and analytics, free forever. When you want more, Pro is €6.99 once and Elite €12.99 once, with an optional €2.99 per month add-on if you want your own domain.',
        'The page itself is where the effort goes. In the [editor](/docs/customization) you work on your actual page: edit text in place, drag sections, links, and widgets into position, and pick layouts, fonts, colors, backgrounds, animated effects, and cursors with every change shown live. Upload tracks and clips so your content plays on the page, and connect YouTube and Twitch so new uploads and live streams show up on their own.'
      ]
    }
  ],
  switchSteps: [
    { title: 'Claim your username', description: 'Sign up free at [mychar.gg/signup](/signup). No card or trial needed.' },
    { title: 'Copy your links', description: 'Add your platforms from the built-in list, unlimited on every plan. Links to other URLs start on Pro.' },
    { title: 'Pick a look', description: 'Choose a layout, fonts, colors, and a background, or start from a community [skin](/skins), and arrange everything by dragging.' },
    { title: 'Replace embeds with uploads', description: 'Upload your music to the player and your videos as clips, then connect YouTube and Twitch. The [audio guide](/docs/profile/audio) covers file types.' },
    { title: 'Swap the link', description: 'Put your mychar.gg link in every bio. If your bio.link page used a custom domain, connect it with the [custom domain add-on](/docs/profile/custom-domain).' }
  ],
  faq: [
    {
      label: 'Does bio.link still have a free plan?',
      content: 'Its site currently shows one Pro plan and a free trial, not a free tier. mychar.gg has a Free plan that stays free, with unlimited platform links, a music player, and analytics.'
    },
    {
      label: 'Is mychar.gg cheaper than bio.link?',
      content: 'Yes, over time. mychar.gg Pro is €6.99 once and Elite €12.99 once. bio.link Pro is $14.99 a month, or $7.49 a month billed yearly. A custom domain is included in bio.link Pro and is €2.99 per month on mychar.gg.'
    },
    {
      label: 'Does mychar.gg have an AI assistant like bio.link?',
      content: 'No. mychar.gg focuses on design and on-page content instead: effects, cursors, a music player, video clips, and integrations.'
    },
    {
      label: 'Can I collect emails on mychar.gg?',
      content: 'No, there are no signup forms or email tools. You can link to a newsletter signup page as a custom link on Pro and Elite.'
    }
  ]
}

export default biolink
